import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useFeedback } from '../hooks/useFeedback';
import {
  FeedbackBar,
  FeedbackBarError,
  FeedbackButton,
  FeedbackForm,
  FeedbackFormActions,
  FeedbackModalCard,
  FeedbackModalClose,
  FeedbackModalHeader,
  FeedbackModalOverlay,
  FeedbackModalTitle,
  FeedbackSubmit,
  FeedbackTextArea,
  FeedbackThanks,
} from '../styles';

type Rating = 'positive' | 'negative' | null;

interface Props {
  sessionId: string;
  // The assistant message id; used as the feedback row's conversation_id.
  conversationId: string;
  userQuery: string;
  llmResponse: string;
}

export function MessageFeedback({ sessionId, conversationId, userQuery, llmResponse }: Props) {
  const [rating, setRating] = useState<Rating>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // Set only for a failed 👍 submission, which -- unlike 👎 -- never opens the modal,
  // so it needs its own error surface instead of relying on the modal's.
  const [positiveFailed, setPositiveFailed] = useState(false);
  const { submitFeedback, isSubmitting, error } = useFeedback();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const positiveButtonRef = useRef<HTMLButtonElement>(null);
  const negativeButtonRef = useRef<HTMLButtonElement>(null);
  const lastRatingButtonRef = useRef<HTMLButtonElement | null>(null);
  const modalCardRef = useRef<HTMLDivElement>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancels the "copied" reset if the bubble unmounts (drawer closed, thread
  // switched) before the 1.5s elapses, so it can't set state on a gone component.
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(llmResponse);
      setCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied by the browser; nothing useful to surface.
    }
  };

  const handlePositive = async () => {
    setRating('positive');
    setPositiveFailed(false);
    const ok = await submitFeedback({
      sessionId,
      conversationId,
      userQuery,
      llmResponse,
      isPositive: true,
    });
    if (ok) {
      setSubmitted(true);
    } else {
      // Don't leave the button looking selected for a rating that didn't save.
      setRating(null);
      setPositiveFailed(true);
    }
  };

  const handleNegative = () => {
    setRating('negative');
    setSubmitted(false);
    setPositiveFailed(false);
    lastRatingButtonRef.current = negativeButtonRef.current;
    setModalOpen(true);
  };

  // Closes the modal. Abandoning before submitting is a full cancel (clears the
  // rating); closing after a successful submit just dismisses the "Thanks"
  // message and leaves the rating in place, same as the sticky 👍 selection.
  const handleClose = useCallback(() => {
    setModalOpen(false);
    setComment('');
    if (!submitted) {
      setRating(null);
    }
    // Return focus to the 👎 button that opened this, so a keyboard/screen-reader
    // user lands back where they were instead of at the top of the document.
    lastRatingButtonRef.current?.focus();
  }, [submitted]);

  const handleSubmit = async () => {
    const ok = await submitFeedback({
      sessionId,
      conversationId,
      userQuery,
      llmResponse,
      isPositive: false,
      comment: comment || undefined,
    });
    if (ok) {
      setSubmitted(true);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }
    textAreaRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      // Trap Tab inside the card: without this, tabbing off either end lands in
      // the transcript/composer behind the overlay, which a modal must not allow.
      if (e.key !== 'Tab' || !modalCardRef.current) {
        return;
      }
      const focusable = modalCardRef.current.querySelectorAll<HTMLElement>(
        'button, textarea, [href], input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modalOpen, handleClose]);

  return (
    <>
      <FeedbackBar $forceVisible={modalOpen || positiveFailed} className="feedback-bar">
        <FeedbackButton
          $active={copied}
          $variant="neutral"
          aria-label="Copy response"
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy response'}
          type="button"
        >
          {copied ? '✅' : '📋'}
        </FeedbackButton>
        <FeedbackButton
          $active={rating === 'positive'}
          $variant="positive"
          aria-label="Good response"
          aria-pressed={rating === 'positive'}
          disabled={isSubmitting}
          onClick={handlePositive}
          ref={positiveButtonRef}
          title="Good response"
          type="button"
        >
          👍
        </FeedbackButton>
        <FeedbackButton
          $active={rating === 'negative'}
          $variant="negative"
          aria-label="Bad response"
          aria-pressed={rating === 'negative'}
          disabled={isSubmitting}
          onClick={handleNegative}
          ref={negativeButtonRef}
          title="Bad response"
          type="button"
        >
          👎
        </FeedbackButton>
        {positiveFailed && (
          <FeedbackBarError role="alert">Couldn’t save feedback. Try again.</FeedbackBarError>
        )}
      </FeedbackBar>

      {modalOpen &&
        createPortal(
          <FeedbackModalOverlay onClick={handleClose}>
            <FeedbackModalCard
              aria-labelledby="feedback-modal-title"
              aria-modal="true"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              ref={modalCardRef}
              role="dialog"
            >
              <FeedbackModalHeader>
                <FeedbackModalTitle id="feedback-modal-title">
                  {submitted ? 'Thanks for your feedback!' : 'How could we improve AskCommit?'}
                </FeedbackModalTitle>
                <FeedbackModalClose aria-label="Close" onClick={handleClose} type="button">
                  ×
                </FeedbackModalClose>
              </FeedbackModalHeader>
              {submitted ? (
                <FeedbackThanks>We’ll use this to help improve future responses.</FeedbackThanks>
              ) : (
                <FeedbackForm>
                  <FeedbackTextArea
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setComment(e.target.value)
                    }
                    placeholder="What was wrong with the response? (optional)"
                    ref={textAreaRef}
                    value={comment}
                  />
                  {error && (
                    <FeedbackThanks>Couldn’t save feedback. Please try again.</FeedbackThanks>
                  )}
                  <FeedbackFormActions>
                    <FeedbackSubmit disabled={isSubmitting} onClick={handleSubmit} type="button">
                      {isSubmitting ? 'Submitting…' : 'Submit feedback'}
                    </FeedbackSubmit>
                  </FeedbackFormActions>
                </FeedbackForm>
              )}
            </FeedbackModalCard>
          </FeedbackModalOverlay>,
          document.body
        )}
    </>
  );
}
